import React, { useContext, useEffect, useCallback, useMemo, useState } from 'react';
import Checker from '../cmp/Checker.js';
import Filter from '../cmp/Filter.js';
import EditCtrl from '../cmp/EditCtrl.js';
import icon from '../icons.svg';
import { dataContext, localestr, toText, load, exportToExcel } from '../utils.js';

function RepCalcs({ data, actions }) {
  const { getWndData = () => ({}), setWndData = () => { } } = actions || {};
  const wndData = data?.wndData || getWndData();
  const { getSettings, setSettings, msg, OpenTabObject, data: contextData } = useContext(dataContext);

  const userSettings = getSettings() || {};
  const [to, setTo] = useState(userSettings.to ? new Date(userSettings.to) : new Date());
  to.setHours(23, 59, 59, 999);

  const context = useContext(dataContext)
  const setPosition = context.setPosition
  const position = context.getPosition('RepCalcs')

  const defaultGrouping = useMemo(
    () => [
      { name: localestr('Продукція'), value: 'p_good_id', checked: true, type: 'catGoods' },
      { name: localestr('Група прод.'), value: 'p_folder_id', checked: false, type: 'fldGoods' },
      { name: localestr('Товар'), value: 'good_id', checked: true, type: 'catGoods' },
      { name: localestr('Група'), value: 'folder_id', checked: false, type: 'fldGoods' },
      { name: localestr('Автор'), value: 'author_id', checked: false, type: 'catUsers' },
      { name: localestr('Тип товару'), value: 'g_type', checked: false, type: 'selGoodType' },
      { name: localestr('Одиниці'), value: 'unit', checked: false, type: 'selUnitType' },
      { name: localestr('Дата'), value: 'date', checked: false, type: 'date' },
      { name: localestr('Документ'), value: 'doc_id', checked: false, type: 'docCalculation' },
    ],
    []
  );

  const filterData = useMemo(
    () => [
      { caption: localestr('Продукція'), dataId: 'p_good_id', typeVal: 'catGoods' },
      { caption: localestr('Товар'), dataId: 'good_id', typeVal: 'catGoods' },
      { caption: localestr('Тип товару'), dataId: 'g_type', typeVal: 'selGoodType' },
      { caption: localestr('Одиниці'), dataId: 'unit', typeVal: 'selUnitType' },
      { caption: localestr('Автор'), dataId: 'author_id', typeVal: 'catUsers' },
    ],
    []
  );

  const grouping = wndData.grouping || userSettings.repCalcs?.grouping || defaultGrouping;

  const handleDateChange = (d) => {
    const newDate = new Date(d);
    setTo(newDate);
    setWndData((prev) => ({ ...prev, to: newDate }));
  };


  const groupingHandler = useCallback(
    (d) => {
      setWndData({ ...wndData, grouping: d });
      setSettings({ repCalcs: { to, grouping: d } });
    },
    [wndData, to, setWndData, setSettings]
  );

  const filterHandler = useCallback(
    (d) => {
      setWndData({ ...wndData, filter: d });
      setSettings({ repCalcs: { to, grouping } });
    },
    [wndData, to, grouping, setWndData, setSettings]
  );

  const getFolders = useCallback((d) => {
    if (!d) return '';
    let ret = `,'${d.id}'`;
    if (d.data) d.data.forEach((el) => (ret += getFolders(el)));
    return ret;
  }, []);

  const processData = useCallback(
    (d) => {
      if (!d) return;

      const { status, msg: errorMsg, groups, filters, data: rawData, to: reportTo } = d;
      if (status === 'OffLine') {
        msg(localestr('Помилка з\'єднання'), localestr('Помилка'), [{ caption: 'OK', action: () => { } }]);
        return;
      }
      if (status === 'error') {
        msg(errorMsg, localestr('Помилка'), [{ caption: 'OK', action: () => { } }]);
        return;
      }

      const cols = groups.map((el) => grouping.find((e) => e.value === el)).filter(Boolean);
      const filterCaptions = filters.map((el) => el.caption);

      let ttl_qtt = 0;
      let ttl_nt = 0;
      const processedData = [];
      const idx = new Array(cols.length > 1 ? cols.length - 1 : 1);
      const keys = new Array(cols.length > 1 ? cols.length - 1 : 1);

      rawData.forEach((val) => {
        if (val.d_t) val.date = val.d_t;
        let prev = false;

        for (let k = 0; k < cols.length - 1; k++) {
          if (keys[k] !== val[cols[k].value] || prev) {
            prev = true;
            keys[k] = val[cols[k].value];
            const key = cols[k];
            const item = {
              id: val[key.value],
              type: key.type,
              name: val[key.value],
              level: k,
              keys: keys.slice(0, k),
              qtt: 0,
              nt: 0,
            };

            item.name = formatName(val, key, contextData);
            item.obj = getObject(key, item.id);
            idx[k] = processedData.length;
            processedData.push(item);
          }
        }

        const key = cols[cols.length - 1];
        const item = {
          id: val[key.value],
          type: key.type,
          name: val[key.value],
          level: cols.length - 1,
          keys: [...keys],
          qtt: parseFloat(val.qtt || 0),
          nt: parseFloat(val.nt || 0),
        };

        item.name = formatName(val, key, contextData);
        item.obj = getObject(key, item.id);
        processedData.push(item);

        for (let k = 0; k < cols.length - 1; k++) {
          processedData[idx[k]].qtt += item.qtt;
          processedData[idx[k]].nt += item.nt;
        }
        ttl_qtt += item.qtt;
        ttl_nt += item.nt;
      });

      processedData.push({
        id: 'totals',
        type: 'totals',
        name: localestr('Разом'),
        level: -1,
        keys: [],
        qtt: ttl_qtt,
        nt: ttl_nt,
      });

      const caption = `${localestr('Звіт по калькуляціях на')} ${new Date(reportTo).toLocaleDateString()}`;
      const headcol = cols.map((el) => el.name).join('/');

      setWndData({ ...wndData, content: { caption, headcol, filters: filterCaptions, data: processedData } });
    },
    [grouping, msg, setWndData, wndData, contextData]
  );

  const loadData = useCallback(
    (d) => {
      if (d) return processData(d);

      const groups = grouping.filter((g) => g.checked).map((g) => g.value);
      if (!groups.length) {
        msg(localestr('Треба вибрати хоча б одне групування'), localestr('Помилка'), [
          { caption: 'OK', action: () => { } },
        ]);
        return;
      }

      const toISO = to.toISOString().substring(0, 19) + 'Z';
      const tzOffset = to.getTimezoneOffset();
      const tz = tzOffset < 0 ? `+0${-tzOffset / 60}:00` : `-0${tzOffset / 60}:00`;

      const filters = (wndData.filter || [])
        .filter((el) => el.compare)
        .map((el) => ({
          dataId: el.dataId,
          compare: el.compare,
          value: el.value,
          caption: `${el.caption} ${formatFilterCaption(el.compare, el.value)}`,
        }));

      load(`action=repCalcs&data=${JSON.stringify({ to: toISO, tz, groups, filters })}`, processData);
    },
    // eslint-disable-next-line 
    [grouping, to, wndData.filter, msg, load, processData]
  );

  const specification = useCallback((obj) => obj && OpenTabObject(obj.type, obj.id), [OpenTabObject]);

  useEffect(() => {
    if (wndData?.run) setTimeout(loadData, 200);
    // eslint-disable-next-line
  }, []);

  const formatName = (val, key, contextData) => {
    const typePrefix = key.type.substring(0, 3);
    if (typePrefix === 'cat' || typePrefix === 'fld') {
      return val[key.value.substring(0, key.value.length - 2) + 'n'] || '...';
    }
    if (typePrefix === 'doc') {
      return `${localestr('Калькуляція')} №${val[key.value]} (${new Date(
        val[key.value.substring(0, key.value.length - 2) + 'd']
      ).toLocaleString()})`;
    }
    if (typePrefix === 'sel') {
      return contextData[key.type]?.find((e) => e.value === val[key.value])?.name || val[key.value];
    }
    return val[key.value];
  };

  const getObject = (key, id) => {
    const typePrefix = key.type.substring(0, 3);
    if (typePrefix === 'cat' && id) return { type: key.type, id };
    if (typePrefix === 'doc' && id) return { type: 'docCalculation', id };
    return null;
  };

  const formatFilterCaption = (compare, value) => {
    if (!value) return compare === '<>' ? 'IS NOT NULL' : 'IS NULL';
    if (['=', '>', '<', '<>'].includes(compare)) return `${compare} ${toText(value)}`;
    if (compare === 'lk') return `${localestr('містить')} ${toText(value)}`;
    if (compare === 'nlk') return `${localestr('не містить')} ${toText(value)}`;
    if (compare === 'in_f') return `${localestr('в папці')} ${toText(value)}`;
    if (compare === 'nin_f') return `${localestr('не в папці')} ${toText(value)}`;
    if (compare === 'in_l') return `${localestr('в списку')} ${toText(value)}`;
    if (compare === 'nin_l') return `${localestr('не в списку')} ${toText(value)}`;
    return '';
  };

  const setPos = target => {
    if (!target) return
    //if(!position) return
    target.scrollTop = position
  }

  const content = wndData.content ? (
    <>
      <h3 className="rep_caption">{wndData.content.caption}</h3>
      {wndData.content.filters.length > 0 && (
        <div className="rep_filters">
          {wndData.content.filters.map((el, idx) => (
            <p key={idx}>{el}</p>
          ))}
        </div>
      )}
      <table className="rep_table">
        <thead>
          <tr>
            <th>{wndData.content.headcol}</th>
            <th>{localestr('Кількість')}</th>
            <th>{localestr('Нетто')}</th>
          </tr>
        </thead>
        <tbody>
          {wndData.content.data.map((el, idx) => (
            <tr tabIndex="0" key={idx}>
              <td
                className={`rep_grp${el.level}`}
                style={el.obj ? { cursor: 'pointer' } : {}}
                onDoubleClick={el.obj ? () => specification(el.obj) : undefined}
              >
                {el.name}
              </td>
              <td className={`rep_grp${el.level} rep_number`}>{el.qtt.toFixed(4)}</td>
              <td className={`rep_grp${el.level} rep_number`}>{el.nt.toFixed(0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  ) : null;

  let btn2 = null
  if (content) {
    btn2 = <div className='no-print' style={{ position: 'absolute', top: '5px', right: '5px', display: 'flex', gap: '5px' }}>
      <div onClick={e => window.print()} style={{ cursor: 'pointer', }}>
        <img style={{ width: '24px', height: '24px' }} src={icon + '#b503'} alt='' />
      </div>
      <div style={{ cursor: 'pointer', }} onClick={() => exportToExcel(wndData)}>
        <img style={{ width: '24px', height: '24px' }} src={icon + '#xlsx'} alt='' />
      </div>

    </div>
  }

  //console.log(wndData)

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'row' }}>
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div className="toolBar no-print">
          <div
            tabIndex="0"
            onClick={() => loadData()}
            onKeyUp={(e) => e.key === 'Enter' && loadData()}
            style={{
              alignSelf: 'center',
              lineHeight: '30px',
              height: '30px',
              width: '95px',
              padding: '0px 2px 0 6px',
              color: '#FFF',
              userSelect: 'none',
              cursor: 'pointer',
              background: `url(${icon}#btn95v) no-repeat`,
              backgroundSize: '102px 30px',
            }}
          >
            <img
              style={{ width: '12px', height: '24px', padding: '2px', verticalAlign: 'middle' }}
              src={`${icon}#run`}
              alt={localestr('Вивести')}
            />{' '}
            {localestr('Вивести')}
          </div>
          <EditCtrl Capt={localestr('на дату')} Value={to} typeVal='datetime' setVal={handleDateChange} width1='55' width2='160' />
          <div style={{ flexGrow: '10' }}></div>
          {/*<div className='reportContent'>{content}{btn2}</div>*/}
        </div>
        <div className='reportContent' style={{ position: 'relative' }} ref={el => { setPos(el) }} onScroll={e => setPosition('RepReturn', e.target.scrollTop)}>{content}{btn2}</div>
      </div>
      <div className="reportTuns no-print">
        <Checker caption={localestr('Групування')} data={grouping} setData={groupingHandler} />
        <Filter filterData={filterData} filter={wndData.filter} setFilter={filterHandler} />
      </div>
    </div>
  );
}

export default RepCalcs;